import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { InteractionWithId } from "../../hooks/useInteractions";
import type { EntityInfo } from "../../hooks/useEntityMap";
import type { EntityType, EventOption, InteractionEvent } from "../../types";
import { nextQuickActions } from "../../utils/eventOptionSelection";
import { calcTotalWaitTime, entityTypeLabel, formatDuration, formatElapsed, formatEventType, formatTime } from "../../utils/tripDisplay";
import { pickStepButtons, useActivityStepOptions, useStepLogger } from "../../hooks/useStepLogger";
import { TypeIcon } from "../common/TypeIcon";
import { StepOrderButton } from "../entities/StepOrderButton";
import "./LiveNowCard.css";

interface LiveNowCardProps {
  activity: InteractionWithId;
  entity: EntityInfo | null;
  type: EntityType | undefined;
  /** What it sits inside, e.g. the park you checked into. */
  parentName: string | null;
  tripId: string;
  zone?: string;
}

/** Re-renders every second while mounted, for the running timer. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function stepLabel(event: InteractionEvent, options: EventOption[]): string {
  if (event.customLabel) return event.customLabel;
  return options.find((o) => o.eventType === event.eventType)?.displayName ?? formatEventType(event.eventType);
}

/**
 * The activity you're in right now, front and centre: how long you've been at
 * the current step, the steps so far, and the next step as one big button.
 * A tap logs straight away and offers Undo instead of asking first.
 */
export function LiveNowCard({ activity, entity, type, parentName, tripId, zone }: LiveNowCardProps) {
  const now = useNow();
  const options = useActivityStepOptions(activity.entityId, type);
  const { events, busy, undo, logStep, undoStep } = useStepLogger(activity.id, activity.events);

  const quick = nextQuickActions(options, events);
  const { primary, secondary } = pickStepButtons(quick.options, quick.branching);

  const first = events[0] ?? null;
  const last = events[events.length - 1] ?? null;
  const sinceFirst = first ? now - first.eventTimestamp.toMillis() : 0;
  // Once the ride (or show, or meal) has started, the line is over: show how
  // long it took, next to what the sign said.
  const waitedSecs = calcTotalWaitTime(events);

  const name = entity?.displayName || activity.name || "Activity";
  const meta = [
    parentName,
    type ? entityTypeLabel(type) : null,
    activity.postedWait ? `posted ${activity.postedWait} min` : null,
  ].filter(Boolean).join(" · ");

  // The trail: the last few steps done, each with how long it lasted (the
  // current one still running), then a hollow slot for what's next.
  const trail = events.slice(-3).map((e, i, list) => {
    const start = e.eventTimestamp.toMillis();
    const nextEvent = list[i + 1] ?? null;
    const current = nextEvent == null;
    const end = nextEvent ? nextEvent.eventTimestamp.toMillis() : now;
    return { event: e, current, ms: end - start };
  });

  return (
    <section className="live-now" aria-label="Happening now">
      <div className="ln-card">
        <div className="ln-head">
          <span className="ln-icon">
            {entity?.imageUrl ? <img src={entity.imageUrl} alt="" /> : <TypeIcon type={type} name={name} tile />}
          </span>
          <Link to={`/trips/${tripId}/interactions/${activity.id}`} className="ln-title">
            <span className="ln-name">{name}</span>
            {meta && <span className="ln-meta">{meta}</span>}
          </Link>
          <span className="ln-live">Live</span>
        </div>

        {last && (
          <div className="ln-timer">
            <span className="ln-clock">{formatElapsed(sinceFirst)}</span>
            <span className="ln-since">since {first ? stepLabel(first, options) : "start"}</span>
          </div>
        )}

        {waitedSecs > 0 && (
          <div className="ln-waited">
            Waited <b>{formatDuration(waitedSecs)}</b>
            {activity.postedWait ? ` · posted ${activity.postedWait}m` : ""}
          </div>
        )}

        <ol className="ln-steps" aria-label="Steps">
          {trail.map(({ event: e, current, ms }, i) => (
            <li key={`${e.eventTimestamp.toMillis()}-${i}`} className={current ? "done current" : "done"}>
              <span className="bar" />
              <span className="lbl">{stepLabel(e, options)}</span>
              <span className="dur">{current ? formatElapsed(ms) : ms < 60_000 ? "<1m" : formatDuration(ms / 1000)}</span>
              <span className="at">{formatTime(e.eventTimestamp.toDate(), e.timezone || zone)}</span>
            </li>
          ))}
          {primary && (
            <li className="next">
              <span className="bar" />
              <span className="lbl">{primary.displayName}</span>
              <span className="dur">next</span>
            </li>
          )}
        </ol>

        <div className="ln-actions">
          {primary ? (
            <button type="button" className="ln-primary" onClick={() => logStep(primary)} disabled={busy}>
              {primary.displayName}
            </button>
          ) : (
            <Link to={`/trips/${tripId}/interactions/${activity.id}`} className="ln-primary">
              Open
            </Link>
          )}
          {secondary.map((opt) => (
            <button key={opt.id} type="button" className="ln-secondary" onClick={() => logStep(opt)} disabled={busy}>
              {opt.displayName}
            </button>
          ))}
        </div>

        {!undo && (
          <div className="ln-foot">
            <span>
              {first ? `Started ${formatTime(first.eventTimestamp.toDate(), first.timezone || zone)}` : ""}
            </span>
            <span className="ln-foot-links">
              <StepOrderButton
                entityId={activity.entityId}
                entityName={entity?.displayName ?? null}
                type={type}
                options={options}
                events={events}
                className="ln-edit"
              />
              <Link to={`/trips/${tripId}/interactions/${activity.id}`} className="ln-more">
                All steps &amp; details →
              </Link>
            </span>
          </div>
        )}
        {undo && (
          <div className="ln-undo" role="status">
            <span>Logged {undo.label}</span>
            <button type="button" onClick={undoStep}>Undo</button>
          </div>
        )}
      </div>
    </section>
  );
}
